<template>
  <div v-on-clickaway="away" class="position-relative">
    <div
      class="player-search-filter-container"
      v-show="isOpen"
      :class="{
        'expanded-height': isLibraryDropdownOpen || isGenreDropdownOpen,
      }"
    >
      <div class="search-filter-clear">
        <a href="#" class="advanced-search-toggle" v-on:click.prevent="close()">
          <i class="fa fa-cogs advanced-search-icon v-align-top"></i>
          <span class="advanced-search-title v-align-top">
            Close Advanced search</span
          >
        </a>
        &nbsp;<a
          href="#"
          class="clear-filter"
          v-on:click.prevent="onClearFilters()"
          style="text-decoration: none"
        >
          <i
            class="material-icons"
            style="font-size: 16px; vertical-align: middle"
            >close</i
          >
          <span
            class="v-align-top"
            style="color: #c9c6c3; font-size: 12px; font-weight: 500"
            >Clear All</span
          >
        </a>
      </div>

      <div class="row mt-3">
        <track-upload
          v-if="!isMobile"
          title="Click to find similar tracks by file upload"
          dark-theme
        ></track-upload>
      </div>

      <div class="filters-checkbox-containers mt-3">
        <div class="d-flex flex-wrap justify-content-between">
          <div class="mb-2" v-for="filter in searchByFilters" :key="filter.id">
            <div class="custom-control custom-checkbox">
              <input
                type="checkbox"
                class="custom-control-input"
                v-bind:value="filter"
                v-model="activeSearchByFilters"
                v-bind:id="'filtering_' + filter.id"
              />
              <label
                class="custom-control-label"
                v-bind:for="'filtering_' + filter.id"
              >
                {{ filter.name }}
              </label>
            </div>
          </div>
        </div>
      </div>

      <div class="search-filter-title mt-3"></div>

      <div>
        <div
          v-for="filter in filterByFilters"
          :key="filter.id"
          v-show="!(filter.id == 'album.id' && !filter.values.length)"
          class="my-2 mx-1"
          v-bind:id="'filter-container-' + filter.id"
        >
          <div class="d-flex align-items-center w-100">
            <label
              class="filter-label mb-0 mr-2"
              v-bind:for="'filtering_' + filter.id"
              style="min-width: 80px"
            >
              {{ filter.name }}
            </label>

            <div class="flex-grow-1 filter-control-width">
              <div class="filters-select-container">
                <!-- Dropdown checkbox filters for Genre and Library -->
                <dropdown-checkbox
                  v-if="filter.useCheckboxes"
                  :key="filter.id"
                  v-model="filter.values"
                  :items="filter.allItems"
                  :placeholder="'Select any ' + filter.namePlural"
                  :filter-id="filter.id"
                  @input="onFilterChange"
                  @dropdown-opened="onDropdownOpened(filter.id)"
                  @dropdown-closed="onDropdownClosed(filter.id)"
                ></dropdown-checkbox>

                <select-typeahead
                  v-else
                  :key="filter.id"
                  v-model="filter.values"
                  v-bind:model="filter.model"
                  v-bind:model-field="filter.modelField"
                  v-bind:placeholder="'Select any ' + filter.namePlural"
                  v-on:selectinstance="
                    onSelectinstanceSet($event, 'WEB_' + filter.id)
                  "
                  v-bind:min-length="0"
                  v-on:input="selectTypeaheadValueUpdated($event, filter)"
                ></select-typeahead>
              </div>
            </div>

            <!-- Clear button for this filter -->
            <a
              href="#"
              class="close text-muted ml-2"
              v-on:click.prevent="clearIndividualFilter(filter)"
              v-if="filter.values.length"
            >
              <i class="material-icons" style="font-size: 20px">close</i>
            </a>
            <div v-else style="width: 34px; min-width: 34px">&nbsp;</div>
          </div>

          <!--
          Exclude Vocals checkbox for mobile/tablet below Genre filter
          <div class="mt-2 d-xl-none" v-if="isGenreFilter(filter)">
            <div class="custom-control custom-checkbox">
              <input
                type="checkbox"
                class="custom-control-input"
                :id="'exclude-vocal-mobile-' + filter.id"
                v-model="excludeVocalTracks"
                @change="onExcludeVocalTracksChange"
              />
              <label
                class="custom-control-label"
                :for="'exclude-vocal-mobile-' + filter.id"
              >
                Exclude Vocals & Songs
              </label>
            </div>
          </div>
          -->
        </div>
      </div>

      <div class="player-search-filter-divider"></div>

      <div class="player-search-filter-duration-container">
        <div class="d-flex align-items-start mt-2">
          <span class="filter-label mr-2"> Duration </span>
          <div class="flex-grow-1">
            <duration-range v-model="durationFilter"></duration-range>
          </div>

          <!-- Clear button for duration - show when either field has a non-empty value -->
          <a
            href="#"
            class="close text-muted duration-clear-button"
            v-on:click.prevent="clearDuration"
            v-if="hasDurationValues"
          >
            <i class="material-icons" style="font-size: 20px">close</i>
          </a>
          <div v-else style="width: 50px">&nbsp;</div>
        </div>
      </div>
    </div>

    <!-- Advanced Search Toggle - shown when panel is closed -->
    <div class="advanced-search-toggle-container">
      <a href="#" class="advanced-search-toggle" v-on:click.prevent="open()">
        <i class="fa fa-cogs advanced-search-icon v-align-middle"></i>
        &nbsp;<span
          class="advanced-search-title v-align-middle d-xs-inline d-lg-none"
          >Advanced</span
        >
        <span class="advanced-search-title v-align-middle d-none d-lg-inline"
          >Advanced Search</span
        >
      </a>
      &nbsp;
      <span
        class="badge badge-pill advanced-search-active-filters-count-badge"
        v-bind:class="{
          'badge-aztec-grey': !activeFiltersCount,
          'badge-info': activeFiltersCount,
        }"
      >
        <span>{{ activeFiltersCount }}</span>
        &nbsp;<span class="font-weight-light">active filters</span> &nbsp;<a
          href="#"
          class="close-icon text-white"
          v-on:click="onClearFilters()"
        >
          <i class="material-icons">close</i>
        </a>
      </span>
      &nbsp;
      <span
        class="badge badge-pill advanced-search-active-filters-count-badge d-none"
        v-if="albumsFiltered == true"
        v-bind:class="{
          'badge-aztec-grey': !albumsFiltered,
          'badge-info': albumsFiltered,
        }"
      >
        &nbsp;<span class="font-weight-light">Clear album filter</span> &nbsp;<a
          href="#"
          class="close-icon text-white"
          v-on:click="onClearAlbumFilters()"
        >
          <i class="material-icons">close</i>
        </a>
      </span>
      <!--
      <span
        class="exclude-vocal-toggle-desktop custom-control custom-checkbox d-none d-xl-inline-block"
        v-if="!isOpen"
      >
        <input
          type="checkbox"
          class="custom-control-input"
          id="exclude-vocal-desktop-header"
          v-model="excludeVocalTracks"
          @change="onExcludeVocalTracksChange"
        />
        <label class="custom-control-label" for="exclude-vocal-desktop-header">
          Exclude Vocals & Songs
        </label>
      </span>
      -->
      <span
        class="btn-group-mobile-header-collapsed d-md-none"
        aria-label="Explore button"
      >
        <button
          type="button"
          class="btn btn-aztec-grey btn-sm"
          v-on:click.prevent="toggleExploreContainer"
        >
          <i class="material-icons v-align-middle">recent_actors</i>
        </button>
      </span>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { playerService } from "../player-service";
import ToastrType from "../toastr-types.interface";
import SelectTypeaheadComponent from "../components/select-typeahead";
import DropdownCheckboxComponent from "../components/dropdown-checkbox";
import TrackUploadComponent from "../components/track-upload";
import DurationRangeComponent from "../components/duration-range";
import { mixin as clickaway } from "vue-clickaway";

export default Vue.component("filtering", {
  name: "filtering",
  mixins: [clickaway],

  data() {
    return {
      isOpen: false,
      displaySearchError: false,
      mobileFilterByActive: false,
      isLibraryDropdownOpen: false,
      isGenreDropdownOpen: false,
      searchByFilters: [
        {
          name: "Track title",
          id: "title",
        },
        {
          name: "Lyrics",
          id: "lyrics",
        },
        {
          name: "Composer",
          id: "composer",
        },
        /*{
						  name: 'Description',
						  id: 'comments'
					  },*/
        /*{
						  name: 'Artist',
						  id: 'artist_names'
					  }*/
      ],
      filterByFilters: [
        {
          name: "Album",
          namePlural: "Albums",
          id: "album.id",
          model: "album",
          modelField: "name",
          values: [],
        },
        {
          name: "Genre",
          namePlural: "Genres",
          id: "genre.id",
          model: "genre",
          modelField: "name",
          values: [],
          exclude: false,
          allItems: [],
          useCheckboxes: true,
        },
        {
          name: "Library",
          namePlural: "Libraries",
          id: "library.id",
          model: "library",
          modelField: "name",
          values: [],
          allItems: [],
          useCheckboxes: true,
        },
      ],
      activeSearchByFilters: [],
      activeFiltersCount: 0,
      defaultDurationFilter: [0, 2000],
      durationFilter: [0, 2000],
      filterByFilterSelectInstances: [],
      filterByFiltersCount: 0,
      excludeVocalTracks: false,
      compiledFilters: false,
      filterMasterClearInProgress: false,
      albumsFiltered: false,
      precompiledFilters: null,
    };
  },

  props: {
    libraries: {
      required: true,
    },
    genres: {
      required: true,
    },
  },

  computed: {
    isMobile() {
      return $("html").hasClass("is-mobile-os") || window.innerWidth < 768;
    },

    hasDurationValues() {
      if (!this.durationFilter || !Array.isArray(this.durationFilter)) {
        return false;
      }
      const [min, max] = this.durationFilter;
      const defaultMax = this.defaultDurationFilter[1];

      // Check if min is set
      const hasMin = !!min;
      // Check if max is set AND different from default max
      const hasMax = max && max !== defaultMax;
      return hasMin || hasMax;
    },
  },

  mounted() {
    let ctx = this;

    // Configure all outgoing Ajax requests
    $.ajaxSetup({
      headers: {
        "X-CSRF-TOKEN": $('meta[name="csrf-token"]').attr("content"),
      },
      cache: false,
    });

    this.onFilterChange();
    this.performFilteringAction();

    // Populate allItems for Genre and Library filters
    this.filterByFilters.forEach((filter) => {
      if (filter.id === "genre.id") {
        filter.allItems = this.genres;
      } else if (filter.id === "library.id") {
        filter.allItems = this.libraries;
      }
    });

    playerService.$on("open-filtering", () => {
      this.open();
    });

    playerService.$on("close-filtering", () => {
      this.close();
    });

    playerService.$on("reset-filters", () => {
      // Clear any existing filters.
      this.resetFilters();

      this.performFilteringAction();
    });

    playerService.$on("perform-filtering-action", () => {
      // Actually run this filter.
      this.performFilteringAction();
    });

    playerService.$on("search-input-invalid", () => {
      // Actually run this filter.
      this.checkSearchInputValid();

      if (this.displaySearchError == true) {
        this.open();
      } else {
        this.close();
      }
    });

    playerService.$on("search-input-valid", () => {
      // Actually run this filter.
      this.checkSearchInputValid();

      if (this.displaySearchError == false) {
        this.close();
      }
    });

    playerService.$on("set-visible-filters", (filterConfig) => {
      // Clear any existing filters. Otherwise it won't yield the expected results.
      this.resetFilters();

      // Set the current active filter
      for (let filter of this.filterByFilters) {
        if (filter.id == filterConfig.id) {
          if (
            typeof this.filterByFilterSelectInstances[filter.id] != "undefined"
          ) {
            // Do an ajax call to get some more info for these selected filters so we can add them to the selectinstance.
            $.ajax({
              method: "POST",
              url: `/api/typeahead/filterinfo`,
              data: {
                model: filter.model,
                modelField: filter.modelField,
                modelIds: filterConfig.filteredList,
              },
              cache: false,
            })
              .done(function (response) {
                if (
                  typeof response.error != "undefined" &&
                  response.error == true
                ) {
                  if (window.adminProjectShare == true) {
                    playerService.toast(
                      `[PLYR_231444] Error: Could not load filtered ids.`,
                      ToastrType.Error,
                    );
                  }
                } else {
                  let filteredItems = $.map(response, function (filteredItem) {
                    filteredItem.text = filteredItem.title || filteredItem.name;

                    return filteredItem;
                  });

                  // Create these new filters.
                  for (let filteredItem of filteredItems) {
                    let newOption = new Option(
                      filteredItem.text,
                      filteredItem.id,
                      true, // preselcted
                      true, // preselcted
                    );

                    ctx.filterByFilterSelectInstances[filter.id]
                      .append(newOption)
                      .trigger("change");
                  }
                }
              })
              .fail(function (responseData) {
                if (window.adminProjectShare == true) {
                  playerService.toast(
                    `[PLYR_231433] Error: Could not load filtered ids.`,
                    ToastrType.Error,
                  );
                }
              })
              .always(function (responseData) {
                // Change the text and show it.
                // playerService.$emit( 'loader-hide' );
              });
          } else {
            console.log(
              "trying to set filters too early! Filter select instances not initialised yet.",
            );
          }
        }
      }
    });

    // When then max duration is changed, udpate it in here.
    playerService.$on("filtering-max-duration-change", (max) => {
      this.defaultDurationFilter[1] = max;
      this.durationFilter[1] = max;
    });

    playerService.$on("albums-filtered", () => {
      this.albumsFiltered = true;
    });

    playerService.$on("albums-not-filtered", () => {
      this.albumsFiltered = false;
    });

    playerService.$on("clear-filters", () => {
      this.onClearFilters();
    });

    setTimeout(function () {
      if ($("body").hasClass("explore-mobile-visible")) {
        $("body").removeClass("explore-mobile-visible");
      }
    }, 400);

    /*

              // On the fly usage.

              playerService.$emit(
                  'set-visible-filters',
                  {
                    id: 'album.id',
                    filteredList: [ 10, 13, 25, 26 ]
                  }
              );

              */
  },

  methods: {
    selectTypeaheadValueUpdated() {
      this.onFilterChange();
    },

    open() {
      this.isOpen = true;
    },

    close: function () {
      // Keep compiled filters/current counts up to date, but do not auto-search.
      this.onFilterChange();

      this.isOpen = false;
    },

    toggle() {
      this.isOpen = !this.isOpen;
    },

    away: function () {
      // Ignore global clickaway events when the advanced filter panel is closed.
      if (this.isOpen !== true) {
        return;
      }

      // Keep compiled filters/current counts up to date, but do not auto-search.
      this.onFilterChange();

      this.isOpen = false;
    },

    onClearFilters() {
      this.resetFilters();
    },

    onClearAlbumFilters() {
      playerService.$emit("clear-album-filter");
    },

    toggleExploreContainer() {
      playerService.$emit("toggle-explore-mobile-visible");
    },

    clearTypeaheadInstance(filter) {
      // Clear the select2/typeahead instance if it exists
      if (typeof this.filterByFilterSelectInstances[filter.id] != "undefined") {
        this.filterByFilterSelectInstances[filter.id]
          .val(null)
          .trigger("change");
      }
      if (
        typeof this.filterByFilterSelectInstances["WEB_" + filter.id] !=
        "undefined"
      ) {
        this.filterByFilterSelectInstances["WEB_" + filter.id]
          .val(null)
          .trigger("change");
      }
    },

    clearFilterByFilters() {
      this.excludeVocalTracks = false;

      // Clear out of selectinstance too.
      for (let filter of this.filterByFilters) {
        filter.values = [];
        if (this.isGenreFilter(filter)) {
          filter.exclude = false;
        }

        this.clearTypeaheadInstance(filter);
      }

      if (!this.filterMasterClearInProgress) {
        this.onFilterChange();
      }
    },

    clearSearchByFilters() {
      this.activeSearchByFilters = [];
    },

    clearDurationFilter() {
      this.durationFilter = [0, this.defaultDurationFilter[1]];
      this.onFilterChange();
    },

    resetFilters() {
      this.filterMasterClearInProgress = true;

      this.clearFilterByFilters();
      this.clearSearchByFilters();
      this.clearDurationFilter();

      this.filterMasterClearInProgress = false;

      // onFilterChange is not needed here, as it is already called by the filter
      // watchers and it causes a reload of the page on reset which is unnecessary
      //this.onFilterChange();

      // this.performFilteringAction();
    },

    calculateActiveFilterCount() {
      this.filterByFiltersCount = 0;

      // Compile the filterByFilters filter.
      for (
        let filterIndex = 0;
        filterIndex < this.filterByFilters.length;
        filterIndex++
      ) {
        if (this.filterByFilters[filterIndex].values.length > 0) {
          this.filterByFiltersCount++;
        }
      }

      if (this.excludeVocalTracks === true) {
        this.filterByFiltersCount++;
      }

      let activeFiltersCount =
        this.activeSearchByFilters.length + this.filterByFiltersCount;

      if (
        !(
          this.durationFilter[0] == this.defaultDurationFilter[0] &&
          this.durationFilter[1] == this.defaultDurationFilter[1]
        )
      ) {
        activeFiltersCount++;
      }

      this.activeFiltersCount = activeFiltersCount;
    },

    onFilterChange() {
      this.checkSearchInputValid();

      if (this.filterMasterClearInProgress == true) {
        return;
      }

      // Setup the compiled filter object.
      let precompiledFilters = {
        searchByFilters: this.activeSearchByFilters,
        filterByFilters: [],
        durationFilter: false,
        excludeVocalTracks: this.excludeVocalTracks === true,
      };

      // Compile the filterByFilters filter.
      for (
        let filterIndex = 0;
        filterIndex < this.filterByFilters.length;
        filterIndex++
      ) {
        let filter = this.filterByFilters[filterIndex];

        if (filter.values.length > 0) {
          precompiledFilters.filterByFilters.push(
            this.buildCompiledFilter(filter),
          );
        }
      }

      // Compile the duration filter.
      if (
        !(
          this.durationFilter[0] == this.defaultDurationFilter[0] &&
          this.durationFilter[1] == this.defaultDurationFilter[1]
        )
      ) {
        precompiledFilters.durationFilter = {
          name: "length",
          id: "length",
          value: {
            min: this.durationFilter[0],
            max: this.durationFilter[1],
          },
        };
      }

      // Now they are compiled, set the filters and emit the change event.
      this.compiledFilters = precompiledFilters;

      // Redo our active filter count so that it's always up to date.
      this.calculateActiveFilterCount();

      this.precompiledFilters = precompiledFilters;

      // this.performFilteringAction();
    },

    onGenreExcludeChange() {
      this.onFilterChange();
    },

    onExcludeVocalTracksChange() {
      this.onFilterChange();
    },

    isGenreFilter(filter) {
      return Boolean(filter) && filter.id === "genre.id";
    },

    clearIndividualFilter(filter) {
      filter.values = [];
      this.clearTypeaheadInstance(filter);
      this.onFilterChange();
    },

    clearDuration() {
      // Reset to default values (0 for min, max track duration for max)
      const defaultMax = this.defaultDurationFilter[1];
      this.durationFilter = [0, defaultMax];
      this.onFilterChange();
    },

    onDropdownOpened(filterId) {
      if (filterId === "library.id") {
        this.isLibraryDropdownOpen = true;
      } else if (filterId === "genre.id") {
        this.isGenreDropdownOpen = true;
      }
    },

    onDropdownClosed(filterId) {
      if (filterId === "library.id") {
        this.isLibraryDropdownOpen = false;
      } else if (filterId === "genre.id") {
        this.isGenreDropdownOpen = false;
      }
    },

    buildCompiledFilter(filter) {
      const compiledFilter = Object.assign({}, filter);

      // Strip UI-only data that should not be sent to the backend
      delete compiledFilter.allItems;
      delete compiledFilter.useCheckboxes;

      if (this.isGenreFilter(compiledFilter)) {
        compiledFilter.exclude = compiledFilter.exclude === true;
      }

      return compiledFilter;
    },

    onSelectinstanceSet(instance, filterId) {
      this.filterByFilterSelectInstances[filterId] = instance;
    },

    performFilteringAction() {
      // Emit to anyone listening that the filters  have changed.
      playerService.$emit("filtering-change", {
        filters: this.precompiledFilters,
        count: this.activeFiltersCount,
      });

      playerService.$emit("reset-sorting", false);

      playerService.$emit("hide-save-project-button");
    },

    checkSearchInputValid() {
      let $searchInput = $(".player-header-container .player-search");

      if ($searchInput.length && $searchInput.val() == "") {
        // Display error
        this.displaySearchError = true;
      } else {
        this.displaySearchError = false;
      }
    },
  },

  watch: {
    activeSearchByFilters() {
      this.onFilterChange();
    },

    // Emits when when our duration filter value updates.
    durationFilter: {
      handler: function (value, old) {
        // Only trigger if the values actually changed
        if (old && value[0] === old[0] && value[1] === old[1]) {
          return;
        }

        this.onFilterChange();
      },
      deep: true,
    },

    filterByFilters() {
      this.onFilterChange();
    },
  },

  components: {
    "select-typeahead": SelectTypeaheadComponent,
    TrackUpload: TrackUploadComponent,
    "duration-range": DurationRangeComponent,
  },
});
</script>
